"use client";

import { create } from "zustand";
import { loginRequest, logoutRequest, socialLoginRequest } from "../services/auth.session";
import { setOnExpire } from "@/shared/http/session-expired";
import { USER_COOKIE } from "@/config/cookies";
import { readClientCookie } from "@/shared/helpers/cookies";
import type { AuthUser, LoginRequestDto, SocialLoginRequestDto } from "../types/auth.dto";

type AuthStatus = "loading" | "authenticated" | "unauthenticated";

interface AuthState {
    user: AuthUser | null;
    status: AuthStatus;
    login: (payload: LoginRequestDto) => Promise<void>;
    loginWithSocial: (payload: SocialLoginRequestDto) => Promise<void>;
    logout: () => Promise<void>;
    /** Reidrata a sessão no carregamento (chamado uma vez pelo AuthInit). */
    hydrate: () => void;
}

/** Lê a identidade não-sensível gravada no cookie auth_user pelo BFF. */
function readUserCookie(): AuthUser | null {
    const raw = readClientCookie(USER_COOKIE);
    if (!raw) return null;

    try {
        return JSON.parse(raw) as AuthUser;
    } catch {
        return null;
    }
}

/**
 * Estado de autenticação para a UI (user + status). Global client-only, como
 * o useAuthModal — nunca lido no SSR, então não vaza entre requests. O access
 * token NÃO fica aqui: vive só em memória no módulo auth.session.
 */
export const useAuthStore = create<AuthState>()((set, get) => {
    // Refresh falhou de vez → reflete logout na UI.
    setOnExpire(() => set({ user: null, status: "unauthenticated" }));

    return {
        user: null,
        status: "loading",

        login: async (payload) => {
            const user = await loginRequest(payload);
            set({ user, status: "authenticated" });
        },

        loginWithSocial: async (payload) => {
            const user = await socialLoginRequest(payload);
            set({ user, status: "authenticated" });
        },

        logout: async () => {
            const { user } = get();
            await logoutRequest(user ? { cpfCnpj: user.cpfCnpj, email: user.email } : null);
            set({ user: null, status: "unauthenticated" });
        },

        hydrate: () => {
            // Reidratação SEM rede: a identidade vem do cookie auth_user. O
            // access token vive num cookie httpOnly e sobrevive ao reload, então
            // não há refresh no carregamento (o antigo gargalo). A validação
            // acontece de graça na primeira chamada autenticada, via proxy; se a
            // sessão tiver morrido, o 401 dispara onExpire e a UI desloga.
            const cached = readUserCookie();
            set(cached ? { user: cached, status: "authenticated" } : { status: "unauthenticated" });
        },
    };
});
